import { Button, ThemeToggle } from '@atreus/ui';
import { Link, useLocation } from '@tanstack/react-router';
import { Building2, LogOut } from 'lucide-react';
import { type ReactNode } from 'react';

import { clearSession, loadSession } from '../auth/session.js';
import { apiPost } from '../cloud.js';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Building2;
}

const navItems: readonly NavItem[] = [
  { to: '/tenants', label: 'Tenants', icon: Building2 },
];

export const AppShell = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const session = loadSession();

  const logout = async () => {
    if (session) {
      try {
        await apiPost('/auth/logout', { refreshToken: session.refreshToken });
      } catch {
        // ignore — limpiamos local de todos modos
      }
    }
    clearSession();
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface text-fg">
      <header className="flex items-center justify-between border-b border-border px-6 py-3">
        <div className="flex items-center gap-3">
          <img
            src="/atreus-logo.svg"
            alt="Atreus"
            className="h-8 w-auto shrink-0 dark:invert"
          />
          <div className="border-l border-border pl-3 leading-tight">
            <div className="text-sm font-bold tracking-tight">Admin</div>
            <div className="text-xs text-fg-muted">{session?.user.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button variant="ghost" size="sm" onClick={logout}>
            <LogOut className="size-4" /> Salir
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <nav className="flex w-56 shrink-0 flex-col border-r border-border bg-surface-muted">
          <ul className="flex-1 space-y-1 p-2">
            {navItems.map((item) => {
              const active = location.pathname.startsWith(item.to);
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium tracking-tight transition-colors ' +
                      (active
                        ? 'bg-fg text-fg-inverse'
                        : 'text-fg hover:bg-surface-raised')
                    }
                  >
                    <Icon className="size-4" />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
};
